import React, { useState } from 'react';
import { View, Text, TextInput, Alert, StyleSheet } from 'react-native';
import theme from '../../theme';
import storage from '../../lib/storage';
import { getBackendUrl } from '../../lib/backendUrl';
import { fetchWithRetry } from '../../lib/fetchWithRetry';
import { I18nContext, useI18n } from '../../i18n/useI18n';
import AppButton from '../../components/AppButton';

async function buildDeviceHeaders() {
  const deviceIdKey = 'kakeibo_device_id';
  let deviceId = await storage.getItem(deviceIdKey);
  if (!deviceId) {
    deviceId = `inst-${Date.now()}-${Math.random().toString(36).slice(2,9)}`;
    try { await storage.setItem(deviceIdKey, deviceId); } catch (_) {}
  }
  const platform = (typeof navigator !== 'undefined' && (navigator.product || 'web')) as string;
  return {
    'X-Device-Id': deviceId,
    'X-App-Id': 'kakeibo',
    'X-Platform': platform,
  } as Record<string,string>;
}

type Props = {
  onSuccess: (payload: { token?: string; user?: any }) => void;
  defaultBackend?: string;
};

export default function LoginForm({ onSuccess, defaultBackend }: Props) {
  const { lang } = React.useContext(I18nContext);
  const { t } = useI18n(lang);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  async function handleSignIn() {
    // validate
    setServerError(null);
    const emailValid = validateEmail(email);
    const passwordValid = validatePassword(password);
    setEmailError(emailValid ? null : t('invalidEmail'));
    setPasswordError(passwordValid ? null : t('passwordMin4'));
    if (!emailValid || !passwordValid) return;
    try {
      setLoading(true);
      const backend = getBackendUrl(defaultBackend);
      const extra = await buildDeviceHeaders();
      const base = backend;
      const res = await fetchWithRetry(`${base}/auth/login`, {
        method: 'POST',
        credentials: 'include', // ensure browser sends/receives cookies
        headers: { 'Content-Type': 'application/json', ...extra },
        body: JSON.stringify({ email, password })
      }, { retries: 2 });
      if (!res.ok) {
        const txt = await res.text();
        setServerError(txt || t('loginFailed'));
        throw new Error(txt || t('loginFailed'));
      }
      const payload = await res.json();
      onSuccess(payload);
    } catch (err: any) {
      if (!serverError) setServerError(err.message || String(err));
      Alert.alert(t('error'), err.message || String(err));
    } finally { setLoading(false); }
  }

  function validateEmail(v: string) {
    return !!v && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  }

  function validatePassword(v: string) {
    return !!v && v.length >= 4;
  }

  return (
    <View style={[styles.card, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}> 
      <Text style={[styles.label, { color: theme.colors.textPrimary }]}>{t('email')}</Text>
      <TextInput value={email} onChangeText={setEmail} autoCapitalize="none" keyboardType="email-address" style={[styles.input, { color: theme.colors.textPrimary, backgroundColor: theme.colors.background, borderColor: theme.colors.border }]} />
      {emailError ? <Text style={styles.error}>{emailError}</Text> : null}
      <Text style={[styles.label, { color: theme.colors.textPrimary }]}>{t('password')}</Text>
      <TextInput value={password} onChangeText={setPassword} secureTextEntry style={[styles.input, { color: theme.colors.textPrimary, backgroundColor: theme.colors.background, borderColor: theme.colors.border }]} />
      {passwordError ? <Text style={styles.error}>{passwordError}</Text> : null}
      {serverError ? <Text style={styles.error}>{serverError}</Text> : null}
      <View style={{ height: 12 }} />
      <AppButton title={loading ? t('pleaseWait') : t('signIn')} onPress={handleSignIn} disabled={loading} tone="primary" />
    </View>
  );
}

const styles = StyleSheet.create({
  card: { width: '100%', maxWidth: 420, padding: 16, borderRadius: 8, borderWidth: 1 },
  label: { marginTop: 8, marginBottom: 4, fontWeight: '600' },
  input: { height: 40, borderWidth: 1, paddingHorizontal: 8, borderRadius: 4 },
  error: { color: theme.colors.error, marginTop: 6 }
});
